import React, { useEffect, useState } from 'react'
import { Alert, Card, Divider, Flex, Grid, Typography } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'

import Loading from './Loading'
import NativeLoginForm from './login/NativeLoginForm'
import NewPasswordForm from './login/NewPasswordForm'
import { ConfirmResetForm, RequestResetForm } from './login/ForgotPasswordForm'
import ProviderButton from './login/ProviderButton'
import {
  confirmPasswordReset,
  getLoginConfig,
  login,
  nativeLogin,
  requestPasswordReset,
  respondToLoginChallenge,
} from '../api/auth-api'

const { Title, Text } = Typography
const { useBreakpoint } = Grid

// The steps of signing in natively. Identity providers are offered on the first
// step only; every other step continues a native sign-in already under way.
const SIGN_IN = 'signIn'
const NEW_PASSWORD = 'newPassword'
const RESET_REQUEST = 'resetRequest'
const RESET_CONFIRM = 'resetConfirm'

const STEP_TEXT = {
  [NEW_PASSWORD]: {
    title: 'auth.setNewPasswordTitle',
    prompt: 'auth.newPasswordRequired',
  },
  [RESET_REQUEST]: {
    title: 'auth.resetPasswordTitle',
    prompt: 'auth.forgotPasswordPrompt',
  },
  [RESET_CONFIRM]: {
    title: 'auth.resetPasswordTitle',
    prompt: 'auth.resetCodePrompt',
  },
}

// Failures the backend marks as needing the user to start again, or as being
// about their account rather than what they typed.
const CHALLENGE_EXPIRED = 'challenge_expired'
const ACCOUNT_REASONS = new Set(['invitation_expired'])

/**
 * Centres its content in the space below the header, both ways.
 */
function LoginPage({ children }) {
  return (
    <Flex id="login-page" justify="center" align="center">
      {children}
    </Flex>
  )
}

function queryParameters() {
  return new URLSearchParams(window.location.search)
}

/**
 * The translated message for a sign-in failure. The backend names every failure
 * it reports with a `code`; its English `message` is never shown. Anything
 * without a known code gets a generic message rather than untranslated text.
 */
function errorMessage(intl, code) {
  const id = `auth.errors.${code}`
  return intl.formatMessage({
    id: code && intl.messages[id] ? id : 'auth.unexpectedError',
  })
}

/**
 * Tracer's sign-in page.
 *
 * Presents the options that are actually available for the application being
 * signed in to: a username and password form, and one button per identity
 * provider discovered on that environment's Cognito user pool. Native users can
 * also set their first password and reset a forgotten one here. Cognito remains
 * the identity authority behind the backend; this page only chooses and
 * presents. The header already shows the Tracer and customer logos.
 */
export default function Login() {
  const intl = useIntl()
  const screens = useBreakpoint()
  const [parameters] = useState(queryParameters())

  const redirectUrl = parameters.get('redirect_url') || '/'
  const requestedProvider = parameters.get('identity_provider')

  const [config, setConfig] = useState(null)
  const [configError, setConfigError] = useState(null)
  const [step, setStep] = useState(SIGN_IN)
  // Carried between steps so the user types it only once.
  const [username, setUsername] = useState('')
  const [notice, setNotice] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let active = true

    getLoginConfig()
      .then((loaded) => {
        if (active) {
          setConfig(loaded)
        }
      })
      .catch(() => {
        if (active) {
          setConfigError(
            intl.formatMessage({ id: 'auth.configurationUnavailable' }),
          )
        }
      })

    return () => {
      active = false
    }
  }, [intl])

  // A link that names a provider keeps its previous behaviour of going straight
  // to that provider instead of asking the user to choose.
  useEffect(() => {
    if (config && requestedProvider) {
      login({ redirect_url: redirectUrl })
    }
  }, [config, redirectUrl, requestedProvider])

  const goTo = (nextStep, nextNotice = null) => {
    setStep(nextStep)
    setNotice(nextNotice)
    setError(null)
  }

  const handleResult = (result) => {
    if (result.status === 'success') {
      window.location = result.redirect_url || '/'
      return
    }

    if (result.status === 'challenge') {
      goTo(NEW_PASSWORD)
      return
    }

    // An administrator reset the password, and Cognito has emailed a code.
    if (result.status === 'password_reset_required') {
      goTo(RESET_CONFIRM, 'auth.adminResetNotice')
      return
    }

    setError({
      type: 'error',
      message: errorMessage(intl, result.code),
    })
  }

  const handleFailure = (failure) => {
    const reason = failure?.response?.data?.reason
    const message = errorMessage(intl, failure?.response?.data?.code)

    // Cognito's challenge has lapsed, so only a fresh sign-in can continue.
    if (reason === CHALLENGE_EXPIRED) {
      setStep(SIGN_IN)
      setNotice(null)
    }

    setError({
      type: ACCOUNT_REASONS.has(reason) ? 'warning' : 'error',
      message,
    })
  }

  const run = (attempt) => {
    setBusy(true)
    setError(null)

    return attempt()
      .then(handleResult)
      .catch(handleFailure)
      .finally(() => setBusy(false))
  }

  const signIn = (values) => {
    const typedUsername = values.username.trim()
    setUsername(typedUsername)

    run(() =>
      nativeLogin({
        redirect_url: redirectUrl,
        username: typedUsername,
        password: values.password,
      }),
    )
  }

  const setNewPassword = ({ new_password }) =>
    run(() => respondToLoginChallenge({ new_password }))

  const sendResetCode = (resetUsername) => {
    setBusy(true)
    setError(null)

    requestPasswordReset({ username: resetUsername })
      .then(() => goTo(RESET_CONFIRM, 'auth.codeSentNotice'))
      .catch(handleFailure)
      .finally(() => setBusy(false))
  }

  const requestReset = ({ username: typedUsername }) => {
    setUsername(typedUsername)
    sendResetCode(typedUsername)
  }

  const confirmReset = ({ code, new_password }) =>
    run(() =>
      confirmPasswordReset({
        username,
        code,
        new_password,
        redirect_url: redirectUrl,
      }),
    )

  // The backend asks the provider for a fresh sign-in every time, so on a shared
  // station a surviving session cannot sign the next person in as the last.
  const selectProvider = (providerName) => {
    setError(null)
    login({
      redirect_url: redirectUrl,
      identity_provider: providerName,
    })
  }

  if (configError) {
    return (
      <LoginPage>
        <Card className="login-card">
          <Alert
            type="error"
            showIcon
            message={configError}
            data-testid="login-configuration-error"
          />
        </Card>
      </LoginPage>
    )
  }

  if (!config) {
    return (
      <Loading
        backgroundColor=""
        text={<FormattedMessage id="processes.authenticating" />}
      />
    )
  }

  const providers = config.providers || []
  const showNative = config.native_enabled !== false
  const hasProviders = providers.length > 0 && step === SIGN_IN

  // The form and the providers sit side by side from the md breakpoint up, and
  // stack on narrower screens.
  const hasBothColumns = hasProviders && showNative
  const sideBySide = hasBothColumns && screens.md !== false

  const renderNativeStep = () => {
    switch (step) {
      case NEW_PASSWORD:
        return (
          <NewPasswordForm
            busy={busy}
            onSubmit={setNewPassword}
            onBack={() => goTo(SIGN_IN)}
          />
        )
      case RESET_REQUEST:
        return (
          <RequestResetForm
            busy={busy}
            initialUsername={username}
            onSubmit={requestReset}
            onBack={() => goTo(SIGN_IN)}
          />
        )
      case RESET_CONFIRM:
        return (
          <ConfirmResetForm
            busy={busy}
            onSubmit={confirmReset}
            onResend={() => sendResetCode(username)}
            onBack={() => goTo(SIGN_IN)}
          />
        )
      default:
        return (
          <NativeLoginForm
            busy={busy}
            initialUsername={username}
            onSubmit={signIn}
            onForgotPassword={() => goTo(RESET_REQUEST)}
          />
        )
    }
  }

  const stepText = STEP_TEXT[step]

  return (
    <LoginPage>
      <Card className={`login-card${hasBothColumns ? ' has-providers' : ''}`}>
        <Flex vertical={true} gap="large">
          <Flex vertical={true} align="center" gap="small">
            <Title level={3} style={{ margin: 0 }}>
              <FormattedMessage
                id={stepText ? stepText.title : 'auth.signin'}
              />
            </Title>
            <Text type="secondary" style={{ textAlign: 'center' }}>
              <FormattedMessage
                id={stepText ? stepText.prompt : 'auth.loginPrompt'}
              />
            </Text>
          </Flex>

          <Flex
            vertical={!sideBySide}
            gap="large"
            className={`login-columns${sideBySide ? ' side-by-side' : ''}`}
          >
            {showNative && (
              <Flex vertical={true} gap="middle" className="login-column">
                {notice && (
                  <Alert
                    type="info"
                    showIcon
                    message={<FormattedMessage id={notice} />}
                    data-testid="login-notice"
                  />
                )}

                {error && (
                  <Alert
                    type={error.type}
                    showIcon
                    message={error.message}
                    data-testid="login-error"
                  />
                )}

                {renderNativeStep()}
              </Flex>
            )}

            {hasBothColumns &&
              (sideBySide ? (
                // antd's vertical Divider cannot carry text, so the side-by-side
                // rule is drawn here; stacked, antd's own Divider is used.
                <div className="login-divider" role="separator">
                  <Text type="secondary">
                    <FormattedMessage id="auth.or" />
                  </Text>
                </div>
              ) : (
                <Divider plain style={{ margin: 0 }}>
                  <FormattedMessage id="auth.or" />
                </Divider>
              ))}

            {hasProviders && (
              <Flex
                vertical={true}
                gap="middle"
                justify="center"
                className="login-providers"
              >
                {/* With the form beside it, the divider's "or" introduces these. */}
                {!showNative && (
                  <Text type="secondary" style={{ textAlign: 'center' }}>
                    <FormattedMessage id="auth.signInWithHeading" />
                  </Text>
                )}

                {providers.map((provider) => (
                  <ProviderButton
                    key={provider.name}
                    provider={provider}
                    busy={busy}
                    onSelect={selectProvider}
                  />
                ))}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Card>
    </LoginPage>
  )
}
